import { trpc } from '@/lib/api/trpc';
import { useEffect } from 'react';
import type { StepType } from '../../storefront/types';
import { PluginBrowser } from './browser/PluginBrowser';
import { PluginInstallationProvider, usePluginInstallation } from './installation/PluginInstallationContext';
import { PluginInstallationOrchestrator } from './installation/PluginInstallationOrchestrator';
import type { Plugin } from './types';

interface PluginMarketplaceV2Props {
  storeId: string;
  onInstallSuccess?: (deploymentId: string, pluginName: string) => void;
  /** When true, shows only public plugins (for unauthenticated users / lead magnet) */
  publicOnly?: boolean;
  /** When true, hides "My Plugins" tab and deploy button (for unauthenticated users) */
  readOnly?: boolean;
  /** Optional callback when user attempts to install while in read-only mode (trigger signup) */
  onInstallAttempt?: (plugin: Plugin, deploymentId?: string, preset?: string) => void;
  /** Optional funnel ID to auto-select when installing from step editor */
  contextFunnelId?: string;
  /** Optional step type to auto-select when installing from step editor */
  contextStepType?: StepType;
  /** Optional callback when config editor should open (instanceId, funnelId) */
  onOpenConfigEditor?: (instanceId: string, funnelId?: string) => void;
  /** Optional callback when config editor closes (used by step editor to close marketplace) */
  onEditorClose?: () => void;
  /** Auto-install plugin from onboarding flow */
  autoInstallPluginId?: string;
  autoInstallDeploymentId?: string;
  autoInstallPreset?: string;
}

/**
 * Inner content component that can access installation context
 */
function PluginMarketplaceContent({
  storeId,
  publicOnly,
  readOnly,
  onInstallAttempt,
  contextFunnelId,
  contextStepType,
  onOpenConfigEditor,
  onEditorClose,
  autoInstallPluginId,
  autoInstallDeploymentId,
  autoInstallPreset,
}: PluginMarketplaceV2Props) {
  const installation = usePluginInstallation();
  const { configEditorOpen, wizardOpen } = installation;

  // Fetch plugins for auto-installation
  const { data: pluginsData } = trpc.plugins.list.useQuery({ storeId }, { enabled: !!autoInstallPluginId });

  // Auto-trigger installation from onboarding query params
  useEffect(() => {
    if (autoInstallPluginId && pluginsData?.plugins && !wizardOpen && !configEditorOpen) {
      const plugin = pluginsData.plugins.find((p) => p.id === autoInstallPluginId);

      if (plugin) {
        console.log('[PluginMarketplace] Auto-triggering installation from onboarding:', {
          pluginId: autoInstallPluginId,
          deploymentId: autoInstallDeploymentId,
          preset: autoInstallPreset,
        });

        // Start installation - this will open wizard
        installation.startInstallation(
          plugin,
          autoInstallDeploymentId,
          undefined, // No context - wizard will collect it
          autoInstallPreset || 'default',
        );
      }
    }
  }, [
    autoInstallPluginId,
    autoInstallDeploymentId,
    autoInstallPreset,
    pluginsData,
    wizardOpen,
    configEditorOpen,
    installation,
  ]);

  // Build installation context if provided
  const installationContext =
    contextFunnelId && contextStepType
      ? {
          funnelId: contextFunnelId,
          stepType: contextStepType,
        }
      : undefined;

  // Hide browser when installation flow is active to prevent visual clutter (stacked dialogs)
  const showBrowser = !configEditorOpen && !wizardOpen;

  return (
    <div className="flex h-full flex-col">
      {/* Browser handles search/display - hidden during installation */}
      {showBrowser && (
        <PluginBrowser
          storeId={storeId}
          publicOnly={publicOnly}
          readOnly={readOnly}
          installationContext={installationContext}
          onInstallAttempt={onInstallAttempt}
        />
      )}

      {/* Orchestrator handles installation flow + config editor */}
      <PluginInstallationOrchestrator
        storeId={storeId}
        contextFunnelId={contextFunnelId}
        onOpenConfigEditor={onOpenConfigEditor}
        onEditorClose={onEditorClose}
        readOnly={readOnly}
      />
    </div>
  );
}

/**
 * V2 of PluginMarketplace with cleaner architecture:
 * - Uses React Context for state management
 * - Separates concerns: Browser vs Installation Flow
 * - Single source of truth for navigation
 * - No nested dialogs or complex callback chains
 */
export function PluginMarketplaceV2(props: PluginMarketplaceV2Props) {
  return (
    <PluginInstallationProvider>
      <PluginMarketplaceContent {...props} />
    </PluginInstallationProvider>
  );
}
